/**
 * QuestionInput — renders the correct input widget based on question type.
 * Dispatches to text, number, select, rating, boolean, and date inputs.
 */
import React from 'react';
import type { QuestionDefinition } from '@/types/pm-types';

export interface QuestionInputProps {
  question: QuestionDefinition;
  value: unknown;
  onChange: (questionId: string, value: unknown) => void;
  disabled?: boolean;
  accentColor?: string;
}

export const QuestionInput: React.FC<QuestionInputProps> = ({
  question,
  value,
  onChange,
  disabled = false,
  accentColor = '#2563eb',
}) => {
  const handleChange = (newValue: unknown) => {
    onChange(question.id, newValue);
  };

  switch (question.type) {
    case 'text':
      return (
        <input
          type="text"
          value={typeof value === 'string' ? value : ''}
          onChange={(e) => handleChange(e.target.value)}
          placeholder={question.placeholder || ''}
          disabled={disabled}
          minLength={question.validation?.minLength}
          maxLength={question.validation?.maxLength}
          pattern={question.validation?.pattern}
          style={{
            width: '100%',
            padding: '8px 12px',
            border: '1px solid #d1d5db',
            borderRadius: 6,
            fontSize: 14,
            outline: 'none',
            background: disabled ? '#f3f4f6' : '#fff',
            transition: 'border-color 0.15s',
          }}
          onFocus={(e) => { e.currentTarget.style.borderColor = accentColor; }}
          onBlur={(e) => { e.currentTarget.style.borderColor = '#d1d5db'; }}
          data-testid={`question-input-${question.id}`}
        />
      );

    case 'number':
      return (
        <input
          type="number"
          value={typeof value === 'number' ? value : ''}
          onChange={(e) => handleChange(e.target.value === '' ? undefined : Number(e.target.value))}
          placeholder={question.placeholder || ''}
          disabled={disabled}
          min={question.validation?.min}
          max={question.validation?.max}
          style={{
            width: '100%',
            padding: '8px 12px',
            border: '1px solid #d1d5db',
            borderRadius: 6,
            fontSize: 14,
            outline: 'none',
            background: disabled ? '#f3f4f6' : '#fff',
            transition: 'border-color 0.15s',
          }}
          onFocus={(e) => { e.currentTarget.style.borderColor = accentColor; }}
          onBlur={(e) => { e.currentTarget.style.borderColor = '#d1d5db'; }}
          data-testid={`question-input-${question.id}`}
        />
      );

    case 'single-select':
      return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }} data-testid={`question-input-${question.id}`}>
          {(question.options || []).map((opt) => {
            const selected = value === opt.value;
            return (
              <label
                key={opt.value}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 8,
                  padding: '8px 12px',
                  border: `1px solid ${selected ? accentColor : '#d1d5db'}`,
                  borderRadius: 6,
                  cursor: disabled ? 'default' : 'pointer',
                  background: selected ? `${accentColor}10` : '#fff',
                  transition: 'all 0.15s',
                  fontSize: 14,
                }}
              >
                <input
                  type="radio"
                  name={question.id}
                  value={opt.value}
                  checked={selected}
                  onChange={() => handleChange(opt.value)}
                  disabled={disabled}
                  style={{ accentColor }}
                />
                {opt.icon && <span>{opt.icon}</span>}
                <span>{opt.label}</span>
                {opt.description && (
                  <span style={{ fontSize: 12, color: '#6b7280', marginLeft: 'auto' }}>{opt.description}</span>
                )}
              </label>
            );
          })}
        </div>
      );

    case 'multi-select': {
      const selectedValues = Array.isArray(value) ? (value as string[]) : [];
      return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }} data-testid={`question-input-${question.id}`}>
          {(question.options || []).map((opt) => {
            const checked = selectedValues.includes(opt.value);
            return (
              <label
                key={opt.value}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 8,
                  padding: '8px 12px',
                  border: `1px solid ${checked ? accentColor : '#d1d5db'}`,
                  borderRadius: 6,
                  cursor: disabled ? 'default' : 'pointer',
                  background: checked ? `${accentColor}10` : '#fff',
                  transition: 'all 0.15s',
                  fontSize: 14,
                }}
              >
                <input
                  type="checkbox"
                  checked={checked}
                  onChange={() => {
                    const next = checked
                      ? selectedValues.filter((v) => v !== opt.value)
                      : [...selectedValues, opt.value];
                    handleChange(next);
                  }}
                  disabled={disabled}
                  style={{ accentColor }}
                />
                {opt.icon && <span>{opt.icon}</span>}
                <span>{opt.label}</span>
                {opt.description && (
                  <span style={{ fontSize: 12, color: '#6b7280', marginLeft: 'auto' }}>{opt.description}</span>
                )}
              </label>
            );
          })}
        </div>
      );
    }

    case 'rating': {
      const ratingValue = typeof value === 'number' ? value : 0;
      const maxRating = question.validation?.max || 5;
      return (
        <div style={{ display: 'flex', gap: 4 }} data-testid={`question-input-${question.id}`}>
          {Array.from({ length: maxRating }, (_, i) => i + 1).map((star) => (
            <button
              key={star}
              type="button"
              onClick={() => !disabled && handleChange(star)}
              disabled={disabled}
              style={{
                background: 'none',
                border: 'none',
                fontSize: 24,
                cursor: disabled ? 'default' : 'pointer',
                color: star <= ratingValue ? '#f59e0b' : '#d1d5db',
                transition: 'color 0.15s',
                padding: 2,
              }}
              aria-label={`${star} star${star > 1 ? 's' : ''}`}
            >
              ★
            </button>
          ))}
          {ratingValue > 0 && (
            <span style={{ fontSize: 14, color: '#6b7280', marginLeft: 8, alignSelf: 'center' }}>
              {ratingValue}/{maxRating}
            </span>
          )}
        </div>
      );
    }

    case 'boolean':
      return (
        <div style={{ display: 'flex', gap: 12, alignItems: 'center' }} data-testid={`question-input-${question.id}`}>
          <button
            type="button"
            onClick={() => !disabled && handleChange(true)}
            disabled={disabled}
            style={{
              padding: '8px 20px',
              border: `1px solid ${value === true ? accentColor : '#d1d5db'}`,
              borderRadius: 6,
              background: value === true ? `${accentColor}15` : '#fff',
              color: value === true ? accentColor : '#374151',
              cursor: disabled ? 'default' : 'pointer',
              fontWeight: value === true ? 600 : 400,
              fontSize: 14,
              transition: 'all 0.15s',
            }}
          >
            Yes
          </button>
          <button
            type="button"
            onClick={() => !disabled && handleChange(false)}
            disabled={disabled}
            style={{
              padding: '8px 20px',
              border: `1px solid ${value === false ? accentColor : '#d1d5db'}`,
              borderRadius: 6,
              background: value === false ? `${accentColor}15` : '#fff',
              color: value === false ? accentColor : '#374151',
              cursor: disabled ? 'default' : 'pointer',
              fontWeight: value === false ? 600 : 400,
              fontSize: 14,
              transition: 'all 0.15s',
            }}
          >
            No
          </button>
        </div>
      );

    case 'date':
      return (
        <input
          type="date"
          value={typeof value === 'string' ? value : ''}
          onChange={(e) => handleChange(e.target.value)}
          disabled={disabled}
          style={{
            width: '100%',
            padding: '8px 12px',
            border: '1px solid #d1d5db',
            borderRadius: 6,
            fontSize: 14,
            outline: 'none',
            background: disabled ? '#f3f4f6' : '#fff',
            transition: 'border-color 0.15s',
          }}
          onFocus={(e) => { e.currentTarget.style.borderColor = accentColor; }}
          onBlur={(e) => { e.currentTarget.style.borderColor = '#d1d5db'; }}
          data-testid={`question-input-${question.id}`}
        />
      );

    default:
      return <span style={{ color: '#9ca3af', fontSize: 13 }}>Unsupported question type</span>;
  }
};

QuestionInput.displayName = 'QuestionInput';
